import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { stitchedDestination } from '@theme/stitched-product-colors';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const crossSellVariant = this.querySelector('.cross-sell-options input[name="id"]');
    if (crossSellVariant instanceof HTMLInputElement) return crossSellVariant.value || null;
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #stitchedController;
  #stitchedRequest = 0;
  #stitchedCache = new Map();
  #stitchedButtonStates = new Map();

  /** Change a tag-linked color in place, using that product's own gallery and cart form. */
  async selectStitchedProduct(product, picker) {
    const url = stitchedDestination(product, new Map(), window.location.href, this.productPageUrl);
    if (!url) return;
    const request = ++this.#stitchedRequest;
    this.#stitchedController?.abort();
    this.#stitchedController = new AbortController();
    this.setAttribute('data-stitched-loading', '');
    picker.setAttribute('aria-busy', 'true');
    for (const button of this.querySelectorAll('quick-add-component button')) {
      if (!this.#stitchedButtonStates.has(button)) this.#stitchedButtonStates.set(button, button.disabled);
      button.disabled = true;
    }
    try {
      const requestUrl = new URL(url);
      requestUrl.searchParams.set('section_id', 'section-rendering-product-card');
      let cached = this.#stitchedCache.get(requestUrl.href);
      if (!cached || cached.expires < Date.now()) {
        const response = await fetch(requestUrl, { signal: this.#stitchedController.signal });
        if (!response.ok) throw new Error('Stitched card unavailable');
        cached = { text: await response.text(), expires: Date.now() + 60_000 };
      }
      if (request !== this.#stitchedRequest || !this.isConnected) return;
      const html = new DOMParser().parseFromString(cached.text, 'text/html');
      const source = html.querySelector('product-card');
      const galleryContent = html.querySelector('[data-stitched-card-gallery]')?.content;
      const gallery = galleryContent?.querySelector('[ref="cardGalleryLink"]');
      const quickAdd = html.querySelector('[data-stitched-card-quick-add]')?.content.querySelector('quick-add-component');
      const price = html.querySelector('product-price [ref="priceContainer"]');
      if (source?.dataset.productId !== product.id || !source.dataset.productTitle || !gallery || !quickAdd || !price) {
        throw new Error('Incomplete stitched card');
      }
      this.#stitchedCache.set(requestUrl.href, cached);
      const title = source.dataset.productTitle;
      const currentSlideshow = this.refs.cardGallery.querySelector('slideshow-component');
      const sizes = this.refs.cardGallery.querySelector('img')?.getAttribute('sizes');
      if (sizes) for (const image of gallery.querySelectorAll('img')) image.setAttribute('sizes', sizes);
      if (currentSlideshow?.hasAttribute('allow-nested')) gallery.querySelector('slideshow-component')?.setAttribute('allow-nested', '');
      // Replace the slideshow instance so its private scroll state matches the new media.
      this.refs.cardGallery?.querySelector('[ref="cardGalleryLink"]')?.replaceWith(gallery.cloneNode(true));
      for (const link of this.querySelectorAll('[ref="productCardLink"], [ref="productTitleLink"], [ref="cardGalleryLink"]')) {
        link.href = url.href;
        if (link.hasAttribute('aria-label')) link.setAttribute('aria-label', title);
      }
      const badges = galleryContent.querySelector('.product-badges');
      const currentBadges = this.refs.cardGallery.querySelector('.product-badges');
      if (badges && currentBadges) morph(currentBadges, badges);
      const sku = html.querySelector('product-sku-component');
      const currentSku = this.querySelector('product-sku-component');
      if (sku && currentSku) morph(currentSku, sku);
      const zoomTitle = this.querySelector('.product-grid-view-zoom-out--details :is(h3, h4)');
      if (zoomTitle) zoomTitle.textContent = title;
      const heading = this.querySelector('[ref="productTitleLink"] .text-block > *');
      if (heading) heading.textContent = title;
      const hiddenTitle = this.refs.productCardLink.querySelector('.visually-hidden');
      if (hiddenTitle) hiddenTitle.textContent = title;
      for (const current of this.querySelectorAll('product-price [ref="priceContainer"]')) morph(current, price);
      const currentQuickAdd = this.querySelector('quick-add-component');
      if (currentQuickAdd) {
        const replacement = quickAdd.cloneNode(true);
        replacement.className = currentQuickAdd.className;
        if (currentQuickAdd.hasAttribute('data-quick-view-only')) {
          replacement.setAttribute('data-quick-view-only', '');
          replacement.dataset.quickAddButton = 'choose';
          replacement.querySelector('.quick-add__button--add')?.remove();
          const chooseButton = currentQuickAdd.querySelector('.quick-add__button--choose')?.cloneNode(true);
          if (chooseButton) {
            const label = chooseButton.getAttribute('aria-label')?.replace(currentQuickAdd.dataset.productTitle, title);
            if (label) chooseButton.setAttribute('aria-label', label);
            const hiddenLabel = chooseButton.querySelector('.visually-hidden');
            if (hiddenLabel && label) hiddenLabel.textContent = label;
            replacement.querySelector('.quick-add__button--choose')?.replaceWith(chooseButton);
          }
        }
        currentQuickAdd.replaceWith(replacement);
      }
      this.dataset.productId = picker.dataset.productId = product.id;
      for (const element of this.querySelectorAll('product-swatches, product-price')) {
        element.dataset.productId = product.id;
        if (element.matches('product-swatches')) element.dataset.productUrl = product.url;
      }
      this.refs.cardGallery.dataset.productId = product.id;
      this.#previousSlideIndex = null;
      this.resetVariant.cancel();
      const mediaUrl = gallery.querySelector('img')?.src;
      if (mediaUrl) this.dataset.featuredMediaUrl = mediaUrl;
    } catch (error) {
      if (request !== this.#stitchedRequest || error.name === 'AbortError') return;
      // Keep the previous complete card usable when a color request fails.
      for (const input of picker.querySelectorAll('input')) input.checked = input.stitchedProduct?.id === this.dataset.productId;
    } finally {
      if (request === this.#stitchedRequest) {
        this.removeAttribute('data-stitched-loading');
        picker.removeAttribute('aria-busy');
        // New buttons retain the server's availability; restore only buttons we disabled.
        for (const [button, disabled] of this.#stitchedButtonStates) {
          if (button.isConnected) button.disabled = disabled;
        }
        this.#stitchedButtonStates.clear();
      }
    }
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#stitchedController?.abort();
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint() && !this.hasAttribute('data-cross-sell-card') && !this.hasAttribute('data-quick-view-card')) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption?.(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker?.(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelector(`product-price [ref='priceContainer']`);
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
      const productUrl = new URL(anchorElement.href);
      const previousUrl = new URL(productCardLink.href);
      if (productUrl.pathname === previousUrl.pathname) {
        for (const [key, value] of previousUrl.searchParams) {
          if (/^(_pos|_sid|_ss|_fid|_psq|_psid|_v)$/.test(key) || key.startsWith('pr_')) {
            productUrl.searchParams.set(key, value);
          }
        }
      }

      productCardLink.href = productUrl.href;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl.href;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl.href;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        const selected = slide.getAttribute('slide-id') === selectedImageId;
        slide.hidden = !selected;
        slide.setAttribute('aria-hidden', String(!selected));
        slide.toggleAttribute('inert', !selected);
        if (selected) {
          for (const image of slide.querySelectorAll('img')) image.loading = 'eager';
        }
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('stitched-color-picker:not([hidden])') || this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.hasAttribute('data-stitched-loading')) {
      event.preventDefault();
      return;
    }

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * @type {{
   *   maxVisibleItems: string | null;
   *   showMoreText: string | null;
   *   moreButtonMarkup: string | null;
   *   wasExpanded: boolean;
   * } | null}
   */
  #overflowSettings = null;

  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');
    this.#cacheOverflowSettings();

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }

    this.#restoreOverflowSettings();
  }

  #cacheOverflowSettings() {
    const { overflowList } = this.refs;

    if (!(overflowList instanceof OverflowList)) return;

    const moreButton = overflowList.querySelector('[slot="more"] .hidden-swatches__count');
    const showMoreText = moreButton instanceof HTMLButtonElement ? moreButton.dataset.showMoreText || null : null;

    this.#overflowSettings = {
      maxVisibleItems: overflowList.getAttribute('max-visible-items'),
      showMoreText,
      moreButtonMarkup: moreButton instanceof HTMLButtonElement ? moreButton.innerHTML : null,
      wasExpanded: overflowList.getAttribute('disabled') === 'true',
    };
  }

  #restoreOverflowSettings() {
    if (!this.#overflowSettings) return;

    const { overflowList } = this.refs;
    if (!(overflowList instanceof OverflowList)) return;

    const { maxVisibleItems, showMoreText, moreButtonMarkup, wasExpanded } = this.#overflowSettings;

    if (maxVisibleItems) {
      overflowList.setAttribute('max-visible-items', maxVisibleItems);
    }

    if (showMoreText) {
      const moreButton = overflowList.querySelector('[slot="more"] .hidden-swatches__count');

      if (moreButton instanceof HTMLButtonElement) {
        moreButton.classList.add('hidden-swatches__count--custom-text');
        moreButton.dataset.showMoreText = showMoreText;
        if (moreButtonMarkup) {
          moreButton.innerHTML = moreButtonMarkup;
        }
      }
    }

    if (wasExpanded) {
      overflowList.showAll();
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#cacheOverflowSettings();

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
